/**
 * AppDetailPage — one deployed managed-app instance, tabbed surface.
 *
 * Mounted at /projects/:projectId/apps/:instanceId as a child of
 * ProjectLayout. Header chrome (title + status pill + meta + actions)
 * matches PipelineDetailPage so the two read as siblings. Below the
 * header is a tab strip — same `.pipeline-detail-tabs/.pipeline-detail-tab`
 * styling, same `?tab=…` URL convention as PipelineDetailPage, so
 * back/forward + share-the-link both work.
 *
 *   Overview       — horizontal meta row (URL / instance id / running /
 *                    resources / storage / hourly cost) + all the
 *                    config sections below it (bootstrap script, env
 *                    vars table, agent-ops scheduler, optional setup
 *                    hint). Mirrors PipelineDetailPage's `pipeline-stat-grid`.
 *   Logs           — 3s-poll tail of agent-ops logs (unchanged from v1)
 *   Conversations  — placeholder (agent-ops MCP doesn't expose a
 *                    list_sessions / get_session tool yet; see render
 *                    body for the explanatory copy + upstream path)
 *
 * Default tab when `?tab` is missing or unknown: overview. Legacy
 * `?tab=config` links redirect to `?tab=overview` on mount so any
 * bookmarked / shared Config-tab links from the previous shape don't
 * land on an empty body.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  arrowBackOutline,
  copyOutline,
  openOutline,
  trashOutline,
  eyeOutline,
  eyeOffOutline,
  pauseOutline,
  playOutline,
  sparklesOutline,
  cloudUploadOutline,
  closeOutline,
} from 'ionicons/icons';
import { IonIcon, IonButton } from '@ionic/react';
import cronstrue from 'cronstrue';
import { Apps } from '../../services/apps';
import { timeAgo } from '../../utils/timeAgo';
import AppStatus from './AppStatus';
import { resolveTier } from '../../components/billing/AppPricingBadge';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
// Reuse the pipeline detail stylesheet for the whole shell (header,
// tab strip, tab body, skeletons, stat grid). Apps marketplace
// stylesheet is still imported for legacy helper classes (e.g.
// `.apps-modal-helper`) used by the section bodies below the meta
// row. Local additions live in AppDetailPage.css.
import '../PipelinesPage/PipelineDetailPage.css';
import '../AppsMarketplacePage/AppsMarketplacePage.css';
import './AppDetailPage.css';

const TABS = [
  { id: 'overview',      label: 'Overview' },
  { id: 'logs',          label: 'Instance log' },
  { id: 'conversations', label: 'Agent activity' },
];

const AppDetailPage = () => {
  const { projectId, instanceId } = useParams();
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [destroying, setDestroying] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Tab state — read from `?tab=…`, fall back to 'overview' on missing
  // / unknown. Mirrors PipelineDetailPage's pattern so users can deep-
  // link any tab.
  const tabFromUrl = searchParams.get('tab');
  const activeTab = TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : 'overview';
  const setActiveTab = (id) => {
    setSearchParams({ tab: id }, { replace: true });
  };

  // Legacy `?tab=config` links → redirect to Overview (Config content
  // got merged into the Overview tab). Replace history so the user
  // can't hit "back" into a dead tab id.
  useEffect(() => {
    if (tabFromUrl === 'config') {
      setSearchParams({ tab: 'overview' }, { replace: true });
    }
  }, [tabFromUrl, setSearchParams]);

  const fetchApp = useCallback(async () => {
    setLoading(true);
    try {
      const data = await Apps.getInstance(instanceId);
      setApp(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch app:', err);
      setError(err);
      setApp(null);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => { fetchApp(); }, [fetchApp]);

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      setToast?.({ isOpen: true, message: `Copied ${label}`, color: 'success' });
    });
  };

  // In-place agent-ops upgrade — re-registers the per-instance task
  // def with the latest image tag and force-redeploys the service.
  // EFS access point + bridgeToken + ALB wiring all preserved by the
  // backend's `upgradeApp` handler (see backend/src/handlers/apps.js).
  // Modal closes on success and we refetch the app to clear the pill.
  const handleUpdate = async () => {
    if (!app || updating) return;
    setUpdating(true);
    try {
      await Apps.upgrade(app.instanceId, { version: app.latestAgentOpsVersion });
      setToast?.({ isOpen: true, message: 'Upgrade started — task will restart in ~1 min', color: 'success' });
      setUpdateModalOpen(false);
      // Refetch so the pill disappears once the new TD is registered.
      // The actual rolling restart takes longer — but the row's
      // agentOpsVersion flips as soon as the new TD is the active one.
      fetchApp();
    } catch (err) {
      setToast?.({ isOpen: true, message: `Upgrade failed: ${err.message}`, color: 'danger' });
    } finally {
      setUpdating(false);
    }
  };

  const handleDestroy = async () => {
    if (!app) return;
    if (!window.confirm(`Destroy app "${app.name || app.instanceId}"? Running task will be stopped. This cannot be undone.`)) return;
    setDestroying(true);
    try {
      await Apps.destroy(app.instanceId);
      setToast?.({ isOpen: true, message: 'App destroyed', color: 'success' });
      navigate(`/projects/${projectId}/apps`);
    } catch (err) {
      setToast?.({ isOpen: true, message: `Destroy failed: ${err.message}`, color: 'danger' });
      setDestroying(false);
    }
  };

  // Loading state — same skeleton shape PipelineDetailPage renders, so
  // the visual continuity holds when the user clicks from the apps
  // list into a row.
  if (loading) {
    return (
      <div className="pipeline-detail-page" aria-busy="true" aria-label="Loading app">
        <div className="pipeline-detail-skel-header">
          <div className="pipeline-detail-skel pipeline-detail-skel--title" />
          <div className="pipeline-detail-skel pipeline-detail-skel--meta" />
        </div>
        <div className="pipeline-detail-skel-body">
          <div className="pipeline-detail-skel pipeline-detail-skel--row" />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '85%' }} />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <NotFoundPage
        code="404"
        title="App not found"
        subtitle="This app instance may have been destroyed, or you don't have access to it."
        primaryAction={{
          label: 'Back to apps',
          to: `/projects/${projectId}/apps`,
          icon: arrowBackOutline,
        }}
      />
    );
  }

  const publicUrl = app.url || (app.subdomain ? `https://${app.subdomain}` : null);

  return (
    <>
      <Helmet>
        <title>{`${app.name || app.appType || instanceId} · Apps · Zibby`}</title>
      </Helmet>

      <div className="pipeline-detail-page">
        {/* Header: title + meta row + actions. Same layout as the
            pipeline detail page so they read as siblings. */}
        <div className="pipeline-detail-header">
          <div className="pipeline-detail-titlerow">
            <h1 className="pipeline-detail-title">
              {app.name || app.appType || instanceId}
            </h1>
            <span className="pipeline-detail-meta-sep">·</span>
            <AppStatus
              status={app.status}
              runningCount={app.runningCount}
              desiredCount={app.desiredCount}
              variant="header"
            />
            <span className="pipeline-detail-meta-sep">·</span>
            <span className="pipeline-detail-version">
              {app.appType}{app.appVersion ? ` v${app.appVersion}` : ''}
            </span>
            {/* agent-ops version is meaningful even when the Update
                pill isn't showing — users want to know what version
                they're on, not just "you're up to date". */}
            {app.agentOpsVersion && (
              <>
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-version" title="agent-ops sidecar version">
                  agent-ops v{app.agentOpsVersion}
                </span>
              </>
            )}
            <span className="pipeline-detail-meta-sep">·</span>
            <span className="pipeline-detail-time">
              Created {app.createdAt ? timeAgo(app.createdAt) : '—'}
            </span>
          </div>
          <div className="pipeline-detail-actions">
            {/* "Update available" pill — mirrors PipelineDetailPage's
                marketplace-update affordance so apps + workflows share
                the same App-Store-style language. Renders only when
                the backend flags the live agent-ops image tag as older
                than LATEST_AGENT_OPS_VERSION. Sits LEFT of the
                primary actions (Fitts' law). */}
            {app.agentOpsUpdateAvailable && (
              <button
                type="button"
                className="pipeline-detail-update-pill"
                onClick={() => setUpdateModalOpen(true)}
                title={`Update available — ${app.agentOpsVersion || '?'} → ${app.latestAgentOpsVersion || '?'}`}
              >
                <IonIcon icon={sparklesOutline} aria-hidden="true" />
                Update available
                {app.latestAgentOpsVersion && (
                  <span className="pipeline-detail-update-pill-ver">
                    v{app.latestAgentOpsVersion}
                  </span>
                )}
              </button>
            )}
            {publicUrl && (
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="header-docs-link"
              >
                <IonIcon icon={openOutline} />
                <span>Open</span>
              </a>
            )}
            <IonButton
              size="small"
              fill="clear"
              className="btn-white"
              disabled={destroying}
              onClick={handleDestroy}
            >
              <IonIcon slot="start" icon={trashOutline} />
              {destroying ? 'Destroying…' : 'Destroy'}
            </IonButton>
          </div>
        </div>

        {updateModalOpen && (
          <div
            className="pipeline-update-overlay"
            onClick={() => !updating && setUpdateModalOpen(false)}
            role="presentation"
          >
            <div
              className="pipeline-update-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="apps-update-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pipeline-update-modal-head">
                <h2 id="apps-update-title" className="pipeline-update-modal-title">
                  Update {app.name || app.appType || app.instanceId}
                </h2>
                <button
                  type="button"
                  className="pipeline-update-modal-close"
                  aria-label="Close"
                  onClick={() => !updating && setUpdateModalOpen(false)}
                  disabled={updating}
                >
                  <IonIcon icon={closeOutline} />
                </button>
              </div>
              <div className="pipeline-update-modal-body">
                <p className="pipeline-update-version-line">
                  <span className="pipeline-update-version-from">
                    v{app.agentOpsVersion || '?'}
                  </span>
                  <span className="pipeline-update-version-arrow">→</span>
                  <span className="pipeline-update-version-to">
                    v{app.latestAgentOpsVersion || '?'}
                  </span>
                </p>
                {/* Apps don't currently surface per-version release
                    notes from the agent-ops repo. Once we wire a
                    GitHub Releases pull-through, drop notes in here. */}
                <p className="pipeline-update-no-notes">
                  Release notes are published in the agent-ops repo.
                </p>
                <p className="pipeline-update-preserved">
                  Your EFS volume, environment variables, and app data
                  are preserved. The container will restart while the
                  new image rolls out (~1 min).
                </p>
              </div>
              <div className="pipeline-update-modal-actions">
                <button
                  type="button"
                  className="pipeline-update-btn pipeline-update-btn--ghost"
                  onClick={() => setUpdateModalOpen(false)}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pipeline-update-btn pipeline-update-btn--primary"
                  onClick={handleUpdate}
                  disabled={updating}
                >
                  <IonIcon icon={cloudUploadOutline} aria-hidden="true" />
                  {updating ? 'Updating…' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab strip — reuse the same classes PipelineDetailPage uses
            so the visual treatment is identical. */}
        <div className="pipeline-detail-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`pipeline-detail-tab ${activeTab === tab.id ? 'pipeline-detail-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div className="pipeline-detail-body">
          {activeTab === 'overview' && (
            <OverviewTab
              app={app}
              instanceId={instanceId}
              publicUrl={publicUrl}
              onCopy={copyToClipboard}
            />
          )}
          {activeTab === 'logs' && (
            <LogsTab app={app} instanceId={instanceId} />
          )}
          {activeTab === 'conversations' && (
            <ConversationsTab instanceId={instanceId} appStatus={app.status} />
          )}
        </div>

      </div>
    </>
  );
};

// ─── Overview tab ──────────────────────────────────────────────────────
// Horizontal meta row (URL / instance / running / resources / storage /
// hourly cost) + the merged-in Config sections below it. Reuses the
// `pipeline-stat-grid` + `pipeline-stat` classes from PipelineDetailPage
// so the two detail pages look like siblings. Per-section bodies below
// the meta row reuse `pipeline-section` / `pipeline-section-title`.

const OverviewTab = ({ app, instanceId, publicUrl, onCopy }) => {
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [storage, setStorage] = useState(null);
  const [storageLoading, setStorageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await Apps.getCatalogEntry(app.appType);
        if (!cancelled) setCatalog(data);
      } catch (err) {
        if (!cancelled) setCatalogError(err.message || 'Failed to load catalog');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [app.appType]);

  useEffect(() => {
    let cancelled = false;
    const fetchStorage = async () => {
      try {
        const data = await Apps.getStorage(instanceId);
        if (!cancelled) setStorage(data);
      } catch (err) {
        // Storage is non-fatal — render `—` and move on.
        if (!cancelled) setStorage({ usedBytes: null });
      } finally {
        if (!cancelled) setStorageLoading(false);
      }
    };
    fetchStorage();
    // Poll at the same cadence agent-ops emits (60s) so the displayed
    // number stays approximately fresh while the user has the tab open.
    const id = setInterval(fetchStorage, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [instanceId]);

  // Resources come from the catalog (the immutable shape the app was
  // deployed against); fall back to whatever the row carries so we
  // still render something while the catalog is loading or if it's
  // gone missing.
  const cpu = catalog?.resources?.cpu ?? app.resources?.cpu;
  const memory = catalog?.resources?.memory ?? app.resources?.memory;
  const cpuVcpu = cpu ? (cpu / 1024).toFixed(cpu % 1024 === 0 ? 0 : 2) : null;
  const memGb = memory ? (memory / 1024).toFixed(memory % 1024 === 0 ? 0 : 2) : null;
  const resourcesText = (cpuVcpu || memGb)
    ? `${cpuVcpu ? `${cpuVcpu} vCPU` : ''}${cpuVcpu && memGb ? ' · ' : ''}${memGb ? `${memGb} GB` : ''}`
    : '—';

  const tier = catalog?.pricing?.tier || app.pricing?.tier || 'standard';
  const tierInfo = resolveTier(tier);

  // agent-ops hard-codes two scheduler entries on first-run bootstrap
  // (visible in agent-ops logs as `hourly_health_check` and
  // `weekly_upgrade`). They don't come from the catalog — they're
  // baked into agent-ops itself — so the strings are hardcoded here.
  // If/when agent-ops exposes a list-tasks MCP tool the right thing
  // to do is query it; for now this is correct because agent-ops
  // always wires these two.
  const schedulerCrons = [
    { id: 'hourly_health_check', cron: '0 * * * *',  description: 'Verify the app is responding; restart on failure.' },
    { id: 'weekly_upgrade',      cron: '0 9 * * 1',  description: 'Pull the latest published catalog version and redeploy.' },
  ];

  const publicUrlNoScheme = publicUrl ? publicUrl.replace(/^https?:\/\//, '') : null;

  return (
    <div className="pipeline-tab">
      {/* Meta row — same flex-wrap stat grid PipelineDetailPage uses.
          Each `.pipeline-stat` is a column (label on top in small caps
          grey, value below). The grid wraps on narrow viewports via
          `flex-wrap: wrap` on `.pipeline-stat-grid`. */}
      <div className="pipeline-stat-grid">
        {publicUrl && (
          <div className="pipeline-stat">
            <div className="pipeline-stat-label">Public URL</div>
            <div className="pipeline-stat-value">
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="pipeline-stat-value-text"
              >
                {publicUrlNoScheme}
              </a>
              <button
                type="button"
                className="pipeline-stat-copy"
                onClick={() => onCopy(publicUrl, 'public URL')}
                title="Copy URL"
              >
                <IonIcon icon={copyOutline} />
              </button>
            </div>
          </div>
        )}

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Instance ID</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">{app.instanceId}</span>
            <button
              type="button"
              className="pipeline-stat-copy"
              onClick={() => onCopy(app.instanceId, 'instance id')}
              title="Copy ID"
            >
              <IonIcon icon={copyOutline} />
            </button>
          </div>
        </div>

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Running</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">
              {(app.runningCount ?? 1)} / {(app.desiredCount ?? 1)}
            </span>
          </div>
          <AppStatus
            status={app.status}
            runningCount={app.runningCount}
            desiredCount={app.desiredCount}
          />
        </div>

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Resources</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">{resourcesText}</span>
          </div>
        </div>

        {/* Storage — actual EFS usage sourced from agent-ops's
            `efs_usage` slog lines. AWS doesn't publish per-access-point
            usage as a metric, so the daemon `du`-walks $StateDir every
            60s and emits a JSON log row; backend filters CloudWatch for
            the newest line. Null while we wait for the first sample
            (fresh deploy / agent-ops <0.1.18). */}
        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Storage</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">
              {storageLoading
                ? '…'
                : (storage && typeof storage.usedBytes === 'number')
                  ? formatBytes(storage.usedBytes)
                  : '—'}
            </span>
          </div>
          <div className="app-overview-substat">
            {storage && storage.sampleAt
              ? `EFS · ${timeAgo(storage.sampleAt)}`
              : 'EFS · auto-scaling'}
          </div>
        </div>

        <div className="pipeline-stat">
          <div className="pipeline-stat-label">Hourly cost</div>
          <div className="pipeline-stat-value">
            <span className="pipeline-stat-value-text">{tierInfo.hourly}</span>
          </div>
          <div className="app-overview-substat">{tierInfo.label}</div>
        </div>
      </div>

      {/* Catalog error banner — non-fatal; the meta row above still
          renders from `app.*`, but the sections below depend on the
          catalog. Keep the user informed instead of silently empty. */}
      {catalogError && (
        <p className="apps-modal-helper app-logs-error">⚠ {catalogError}</p>
      )}

      {/* Bootstrap script + Env vars + Setup hint removed from end-user
          view — these are catalog internals (install commands, default
          host/port env, prose explaining n8n's onboarding). Users can't
          change them and don't need to see them. Available via CLI:
          `zbx app templates` shows the catalog entry verbatim. */}

      {/* agent-ops scheduler */}
      <div className="page-card app-detail-card">
        <div className="card-header"><h2>agent-ops scheduler</h2></div>
        <p className="apps-modal-helper">
          Every Zibby-managed app runs an in-container scheduler. These two
          jobs are wired automatically; they cannot be edited from the
          dashboard yet.
        </p>
        <table className="app-config-envs">
          <thead>
            <tr><th>Job</th><th>Cron</th><th>Translated</th></tr>
          </thead>
          <tbody>
            {schedulerCrons.map((c) => (
              <tr key={c.id}>
                <td><code className="app-detail-mono">{c.id}</code></td>
                <td><code className="app-detail-mono">{c.cron}</code></td>
                <td>
                  {safeCronstrue(c.cron)}
                  <div className="app-config-cron-hint">{c.description}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Logs tab ──────────────────────────────────────────────────────────
// Polling-based tail of the agent-ops + app container logs via
// GET /apps/:id/logs. Unchanged from v1 — only moved into a tab body.
// Hidden entirely for instances that can't have logs (stub-mode
// services, stopped/destroyed rows).

const LogsTab = ({ app, instanceId }) => {
  // Logs are paginated by CloudWatch `nextToken` (here called `cursor`).
  // First fetch returns the newest page; subsequent "load older" fetches
  // pass the prior cursor and PREPEND older lines onto the existing
  // buffer. Live-tail polling only runs on the newest page (cursor=null
  // AND filter cleared) — once the user starts browsing history we
  // freeze the polling so their scroll position doesn't get stomped.
  const [logs, setLogs] = useState([]);                 // flat array of "<iso>  <message>"
  const [cursor, setCursor] = useState(null);           // next page token (for older lines)
  const [hasMore, setHasMore] = useState(false);        // matches the last response's hasMore
  const [logsLoading, setLogsLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [logsPaused, setLogsPaused] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [logsFilter, setLogsFilter] = useState('');       // raw input value
  const [appliedFilter, setAppliedFilter] = useState(''); // debounced, server-side
  // Skeleton gate — true once the first fetch resolves (success or
  // error). Subsequent 3s polls / filter refetches don't flip it back,
  // so the skeleton only flashes on initial mount, not on every
  // refresh or every keystroke in the filter box.
  const [hasLoaded, setHasLoaded] = useState(false);
  const logsBodyRef = useRef(null);
  const stickyBottomRef = useRef(true);
  // Refs mirror the cursor/loading state for callbacks that close over
  // the initial render's values (scroll handler, polling loop).
  const cursorRef = useRef(cursor);
  const hasMoreRef = useRef(hasMore);
  const loadingOlderRef = useRef(loadingOlder);
  useEffect(() => { cursorRef.current = cursor; }, [cursor]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { loadingOlderRef.current = loadingOlder; }, [loadingOlder]);

  // Debounce the user's keystrokes 250ms before kicking off a fresh
  // server-side filter query.
  useEffect(() => {
    const id = setTimeout(() => setAppliedFilter(logsFilter.trim()), 250);
    return () => clearTimeout(id);
  }, [logsFilter]);

  // Live-tail polling — only active when we're on the newest page
  // (cursor=null) so scrolling into history doesn't get clobbered every
  // 3s. Pause/Resume toggle still wins.
  useEffect(() => {
    if (!instanceId || !app) return undefined;
    const serviceArn = app.serviceArn || '';
    const isStub = serviceArn.includes(':stub:');
    if (isStub) return undefined;
    if (['stopped', 'destroyed', 'failed'].includes(app.status)) return undefined;
    if (logsPaused) return undefined;

    let cancelled = false;
    const fetchLatest = async () => {
      const node = logsBodyRef.current;
      if (node) {
        const slack = 24;
        stickyBottomRef.current = (node.scrollHeight - node.scrollTop - node.clientHeight) <= slack;
      }
      setLogsLoading(true);
      try {
        const data = await Apps.getLogs(instanceId, {
          lines: 200,
          filter: appliedFilter || undefined,
        });
        if (cancelled) return;
        const lines = Array.isArray(data?.lines) ? data.lines : [];
        setLogs(lines);
        setCursor(data?.nextToken || null);
        setHasMore(!!data?.hasMore);
        setLogsError(null);
      } catch (err) {
        if (cancelled) return;
        setLogsError(err.message || 'Failed to fetch logs');
      } finally {
        if (!cancelled) {
          setLogsLoading(false);
          setHasLoaded(true);
        }
      }
    };

    fetchLatest();
    const id = setInterval(fetchLatest, 3000);
    return () => { cancelled = true; clearInterval(id); };
    // appliedFilter triggers a re-fetch from scratch when the user
    // changes the search box (debounced upstream).
  }, [instanceId, app, logsPaused, appliedFilter]);

  // Load-older handler — kicks off when the scroll position is near the
  // top of the buffer (or via the IntersectionObserver sentinel below).
  // Guarded by hasMore + loadingOlder to avoid duplicate fan-out.
  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current) return;
    if (!hasMoreRef.current) return;
    if (!cursorRef.current) return;
    setLoadingOlder(true);
    // Remember the current scroll offset so we can restore it after
    // prepending older lines (otherwise the viewport jumps up).
    const node = logsBodyRef.current;
    const prevScrollHeight = node ? node.scrollHeight : 0;
    const prevScrollTop = node ? node.scrollTop : 0;
    try {
      const data = await Apps.getLogs(instanceId, {
        lines: 200,
        cursor: cursorRef.current,
        filter: appliedFilter || undefined,
      });
      const olderLines = Array.isArray(data?.lines) ? data.lines : [];
      setLogs((prev) => [...olderLines, ...prev]);
      setCursor(data?.nextToken || null);
      setHasMore(!!data?.hasMore);
      // After React paints the prepended rows, restore scroll position
      // so the user's viewport stays anchored on the line they were
      // looking at — they only see new content sliding in from the top.
      requestAnimationFrame(() => {
        const n2 = logsBodyRef.current;
        if (!n2) return;
        const delta = n2.scrollHeight - prevScrollHeight;
        n2.scrollTop = prevScrollTop + delta;
      });
    } catch (err) {
      setLogsError(err.message || 'Failed to fetch older logs');
    } finally {
      setLoadingOlder(false);
    }
  }, [instanceId, appliedFilter]);

  // Scroll-position detector: when the user scrolls within 50px of the
  // top of the buffer, request the next older page. Cheap onScroll +
  // ref check (no IntersectionObserver needed for one trigger zone).
  useEffect(() => {
    const node = logsBodyRef.current;
    if (!node) return undefined;
    const onScroll = () => {
      if (node.scrollTop < 50) loadOlder();
    };
    node.addEventListener('scroll', onScroll);
    return () => node.removeEventListener('scroll', onScroll);
  }, [loadOlder]);

  // Auto-scroll to bottom on live-tail updates only — and only when the
  // user is already near the bottom (sticky-tail). If they've scrolled
  // up to browse history, leave them where they are.
  useEffect(() => {
    if (!stickyBottomRef.current) return;
    const node = logsBodyRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [logs]);

  const isStub = String(app.serviceArn || '').includes(':stub:');
  const isFinal = ['stopped', 'destroyed', 'failed'].includes(app.status);
  if (isStub || isFinal) {
    return (
      <div className="pipeline-tab">
        <div className="page-card app-detail-card">
          <div className="card-header"><h2>Instance log</h2></div>
          <p className="apps-modal-helper">
            {isFinal
              ? `This instance is ${app.status} — no live logs to tail.`
              : 'Logs are not available for stub instances.'}
          </p>
        </div>
      </div>
    );
  }

  // Pre-compute the widest line-number column so all numbers align.
  const gutterWidth = Math.max(3, String(logs.length).length);
  // Live-polling indicator: mirror the EXACT conditions the polling
  // useEffect uses to decide whether to keep ticking. `cursor` is set on
  // every successful fetch (CloudWatch's nextToken) so it's almost never
  // null — that's not the right gate. The real gates are: app loaded,
  // not a stub service, status is alive, and user hasn't paused.
  const isPollingLive = !logsPaused
    && !!app
    && !(app.serviceArn || '').includes(':stub:')
    && !['stopped', 'destroyed', 'failed'].includes(app.status);
  return (
    <div className="pipeline-tab">
      <div className="page-card app-detail-card">
        {/* No <h2> title — the tab strip above already reads
            'INSTANCE LOG', a card-level title repeats it. Filter input
            takes the title slot on the left; status + control buttons
            sit on the right. */}
        <div className="card-header app-logs-header">
          <input
            type="text"
            className="app-logs-ctl app-logs-ctl--input"
            placeholder="Filter…"
            value={logsFilter}
            onChange={(e) => setLogsFilter(e.target.value)}
            aria-label="Filter logs"
          />
          <div className="app-logs-controls">
            {isPollingLive && (
              <span className="app-logs-live" title="Polling every 3s">Live</span>
            )}
            <button
              type="button"
              className="app-logs-ctl app-logs-ctl--btn"
              onClick={() => setLogsPaused((p) => !p)}
              aria-pressed={logsPaused}
            >
              <IonIcon icon={logsPaused ? playOutline : pauseOutline} />
              <span>{logsPaused ? 'Start' : 'Stop'}</span>
            </button>
          </div>
        </div>

        <pre ref={logsBodyRef} className="app-logs-body">
          {/* Top sentinel — load-older indicator and end-of-history mark. */}
          {hasLoaded && logs.length > 0 && (
            <div className="app-logs-loader">
              {loadingOlder
                ? 'Loading older lines…'
                : (hasMore ? 'Scroll up to load older lines…' : '— End of history —')}
            </div>
          )}
          {!hasLoaded ? (
            // Initial-load skeleton: 8 monospace shimmer rows with a
            // matching gutter so when the real logs paint there's no
            // layout shift. Random-ish widths (66/82/74/…) so it reads
            // as "varied log lines", not three identical bars.
            <div className="app-logs-skeleton" aria-hidden>
              {[66, 82, 74, 90, 58, 78, 86, 64].map((w, i) => (
                <div key={i} className="app-logs-line app-logs-skel-line">
                  <span
                    className="app-logs-lineno app-logs-skel-lineno"
                    style={{ width: '3ch' }}
                  />
                  <span
                    className="app-logs-linetext app-logs-skel-bar"
                    style={{ width: `${w}%` }}
                  />
                </div>
              ))}
            </div>
          ) : logsError ? (
            <span className="app-logs-error">⚠ {logsError}</span>
          ) : logs.length === 0 ? (
            <span className="app-logs-empty">
              {appliedFilter
                ? `No lines match "${appliedFilter}".`
                : (app.status === 'provisioning'
                  ? 'Waiting for agent-ops to emit first log line…'
                  : 'No log events found.')}
            </span>
          ) : (
            logs.map((line, i) => (
              <div key={i} className="app-logs-line">
                <span className="app-logs-lineno" style={{ width: `${gutterWidth}ch` }}>
                  {i + 1}
                </span>
                <span className="app-logs-linetext">{line}</span>
              </div>
            ))
          )}
        </pre>
      </div>
    </div>
  );
};

// Env-vars table row — masks sensitive values, with a hover-to-reveal
// button (same pattern as the API tokens page).
const EnvRow = ({ envKey, envValue }) => {
  const isSecret = /_TOKEN|_KEY|_SECRET/.test(envKey);
  const [revealed, setRevealed] = useState(false);
  const show = !isSecret || revealed;
  return (
    <tr>
      <td><code className="app-detail-mono">{envKey}</code></td>
      <td>
        <span className="app-config-env-value">
          {show ? String(envValue) : '••••••••'}
        </span>
        {isSecret && (
          <button
            type="button"
            className="app-detail-copy-btn"
            onClick={() => setRevealed((r) => !r)}
            title={revealed ? 'Hide' : 'Reveal'}
          >
            <IonIcon icon={revealed ? eyeOffOutline : eyeOutline} aria-hidden="true" />
          </button>
        )}
      </td>
    </tr>
  );
};

// ─── Conversations tab ─────────────────────────────────────────────────
// Placeholder. The running agent-ops MCP exposes 10 builtin tools
// (agent_status, agent_run_now, agent_history, agent_logs,
// agent_list_tasks, agent_set_task, agent_get_task, agent_get_mission,
// agent_set_mission, agent_remember_fact) plus host_* shell/http
// proxies — but no list_sessions / get_session for the per-Claude-CLI
// transcripts that show up in container logs. Surfacing those needs
// an upstream agent-ops change.

/**
 * ConversationsTab — Claude CLI conversation viewer.
 *
 * Data source: the same CloudWatch log stream the Logs tab polls. We
 * filter for the `claudecli: conversation complete` slog records that
 * agent-ops v0.1.17+ emits AFTER every Claude CLI run. Each record
 * carries the system_prompt / user_prompt / final result / num_turns
 * / total_cost_usd, all parsed from the structured slog payload.
 *
 * Why not call agent-ops MCP for proper session history (full
 * intermediate-turn transcripts, tool calls): the ALB routes the
 * `/_zibby_ops/*` prefix to agent-ops without stripping it, and
 * agent-ops only listens on `/`-rooted paths. So `POST /apps/:id/mcp`
 * → `/_zibby_ops/mcp` → 404 from agent-ops. Fixing that needs an
 * agent-ops upstream change (add prefixed routes) — not blocking the
 * conversation viewer today, just limits richness to "final summary
 * per run" instead of "every turn + tool call."
 */
const ConversationsTab = ({ instanceId, appStatus }) => {
  const [convos, setConvos] = useState([]);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  // hasLoaded gates the skeleton — true once the first fetch resolves
  // (success OR error). Subsequent 3s polls don't flip it, so the
  // skeleton only flashes on the initial mount, not on every refresh.
  const [hasLoaded, setHasLoaded] = useState(false);

  // 3s polling — no pause toggle (agent runs land at the top hourly,
  // never clobbering an expanded row below). Skip when the instance
  // is stopped/destroyed.
  useEffect(() => {
    if (!instanceId) return undefined;
    if (['stopped', 'destroyed', 'failed'].includes(appStatus)) return undefined;

    let cancelled = false;
    const fetchAndParse = async () => {
      try {
        // Pull a wide window — claudecli runs hourly, so 1000 lines
        // covers a few days of conversations without paginating.
        const data = await Apps.getLogs(instanceId, { lines: 1000 });
        if (cancelled) return;
        const parsed = parseConversations(Array.isArray(data?.lines) ? data.lines : []);
        setConvos(parsed);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to fetch logs');
      } finally {
        if (!cancelled) setHasLoaded(true);
      }
    };

    fetchAndParse();
    const id = setInterval(fetchAndParse, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [instanceId, appStatus]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div className="pipeline-tab">
      <div className="page-card app-detail-card">
        <div className="card-header app-logs-header">
          <h2>Agent activity</h2>
          <div className="app-logs-controls">
            {/* No pause/stop toggle here — new runs arrive ~hourly and
                land at the top of the list without clobbering anything
                expanded below, so the pause button has nothing to do.
                Just the polling indicator. */}
            <span className="app-logs-live" title="Polling every 3s">Live</span>
          </div>
        </div>

        {!hasLoaded ? (
          // Skeleton: 3 ghost conversation rows shimmering while the
          // first fetch is in-flight. We don't know the real count yet
          // — 3 is enough to read as "list", small enough to avoid
          // pretending we have data. Once `hasLoaded` flips (success or
          // error), this is replaced by the real list / empty / error.
          <ul className="app-convo-list app-convo-skeleton" aria-hidden>
            {[0, 1, 2].map((i) => (
              <li key={i} className="app-convo-item app-convo-skel-item">
                <div className="app-convo-summary app-convo-skel-summary">
                  <div className="app-convo-time app-convo-skel-bar app-convo-skel-bar--time" />
                  <div className="app-convo-headline app-convo-skel-bar app-convo-skel-bar--headline" />
                  <div className="app-convo-meta app-convo-skel-bar app-convo-skel-bar--meta" />
                </div>
              </li>
            ))}
          </ul>
        ) : error ? (
          <p className="app-logs-error">⚠ {error}</p>
        ) : convos.length === 0 ? (
          <p className="apps-modal-helper">
            No conversations yet. agent-ops fires <code className="app-detail-mono">hourly_health_check</code> at
            the top of every hour (UTC); the next run will emit a
            <code className="app-detail-mono"> claudecli: conversation complete </code>
            line that lands here.
          </p>
        ) : (
          <ul className="app-convo-list">
            {convos.map((c) => {
              const isOpen = expanded.has(c.id);
              const cost = c.totalCostUsd != null
                ? `$${c.totalCostUsd < 0.01 ? c.totalCostUsd.toFixed(4) : c.totalCostUsd.toFixed(2)}`
                : '—';
              return (
                <li key={c.id} className={`app-convo-item ${isOpen ? 'is-open' : ''}`}>
                  <button type="button" className="app-convo-summary" onClick={() => toggle(c.id)}>
                    <div className="app-convo-time">{c.localTime}</div>
                    <div className="app-convo-headline">
                      {c.userPromptPreview || c.systemPromptPreview || '(no prompt)'}
                    </div>
                    <div className="app-convo-meta">
                      <span>{c.numTurns ?? '?'} turn{c.numTurns === 1 ? '' : 's'}</span>
                      <span>{cost}</span>
                      {c.isError && <span className="app-convo-err">error</span>}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="app-convo-body">
                      {c.systemPrompt && (
                        <div className="app-convo-section">
                          <div className="app-convo-section-label">Agent context</div>
                          {/* Plain-English clarifier: what users see is NOT
                              the full system prompt the model received.
                              Claude Code CLI auto-injects its own preamble
                              (tool schemas, output format, safety rules)
                              that's hardcoded in the `claude` binary and
                              not reachable from us. This block is only
                              the slice agent-ops prepends on top — the
                              instance's MISSION + KNOWN FACTS log + base
                              guidance. Truncated at the agent-ops side. */}
                          <div className="app-convo-section-hint">
                            What agent-ops prepended for this run. Claude Code CLI's built-in
                            preamble (tool schemas, output format, safety) is invisible to us.
                          </div>
                          <pre className="app-convo-text">{c.systemPrompt}</pre>
                        </div>
                      )}
                      {c.userPrompt && (
                        <div className="app-convo-section">
                          <div className="app-convo-section-label">User prompt</div>
                          <pre className="app-convo-text">{c.userPrompt}</pre>
                        </div>
                      )}
                      {c.result && (
                        <div className="app-convo-section">
                          <div className="app-convo-section-label">Claude's response</div>
                          <pre className="app-convo-text app-convo-result">{c.result}</pre>
                        </div>
                      )}
                      {c.stderr && (
                        <div className="app-convo-section">
                          <div className="app-convo-section-label">stderr</div>
                          <pre className="app-convo-text">{c.stderr}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

/**
 * Parse the raw `<ISO timestamp>  <JSON message>` lines from the logs
 * endpoint, filter for `msg=claudecli: conversation complete`, and
 * pull out the structured fields. Newest first.
 *
 * Tolerant of: unparseable JSON (skips), missing fields (renders '?'),
 * lines without two-space separator (skips).
 */
function parseConversations(lines) {
  const out = [];
  for (const raw of lines) {
    const idx = raw.indexOf('  ');
    if (idx < 0) continue;
    const tsStr = raw.slice(0, idx).trim();
    const body = raw.slice(idx + 2).trim();
    if (!body.startsWith('{')) continue;
    let obj;
    try { obj = JSON.parse(body); } catch { continue; }
    if (obj?.msg !== 'claudecli: conversation complete') continue;

    const userPrompt = obj.user_prompt || '';
    const systemPrompt = obj.system_prompt || '';
    out.push({
      id: `${tsStr}-${out.length}`,
      ts: tsStr,
      localTime: formatLocalTime(tsStr),
      systemPrompt,
      systemPromptPreview: previewLine(systemPrompt),
      userPrompt,
      userPromptPreview: previewLine(userPrompt),
      result: obj.result || '',
      numTurns: typeof obj.num_turns === 'number' ? obj.num_turns : null,
      totalCostUsd: typeof obj.total_cost_usd === 'number' ? obj.total_cost_usd : null,
      isError: !!obj.is_error,
      stderr: obj.stderr || '',
    });
  }
  // Newest first.
  out.sort((a, b) => (a.ts < b.ts ? 1 : -1));
  return out;
}

function previewLine(s) {
  if (!s) return '';
  const trimmed = String(s).split('\n')[0].trim();
  return trimmed.length > 120 ? trimmed.slice(0, 117) + '…' : trimmed;
}

function formatLocalTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────

function formatBytes(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const v = n / Math.pow(1024, i);
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

function safeCronstrue(expr) {
  try { return cronstrue.toString(expr, { use24HourTimeFormat: true }); }
  catch { return expr; }
}

export default AppDetailPage;
